// Argus — AppShell: layout dual sidebar + main canvas pentru floating windows.
// Skeleton minimal Faza 1 — fără AgentSidebar / WindowManager (livrate Faza 2 + 3).

import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminModalsStore } from '../../store/adminModalsStore';
import {
  AppBar,
  Box,
  Button,
  Drawer,
  IconButton,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  Divider,
} from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LogoutIcon from '@mui/icons-material/Logout';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GridViewIcon from '@mui/icons-material/GridView';
import { NotificationsBell } from './NotificationsBell';
import { WindowsTaskbar } from './WindowsTaskbar';

const SIDEBAR_WIDTH = 280;

export interface AppShellProps {
  themeMode: 'dark' | 'light';
  onToggleTheme: () => void;
  userName: string;
  userRole: string;
  onLogout: () => void;
  sidebar?: ReactNode;
  children?: ReactNode;
}

export function AppShell({
  themeMode,
  onToggleTheme,
  userName,
  userRole,
  onLogout,
  sidebar,
  children,
}: AppShellProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const handleAvatarClick = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const navigate = useNavigate();
  const openUpdates = useAdminModalsStore((s) => s.openUpdates);
  const openMyProfile = useAdminModalsStore((s) => s.openMyProfile);
  const openPermissions = useAdminModalsStore((s) => s.openPermissions);
  const openWatchedPaths = useAdminModalsStore((s) => s.openWatchedPaths);
  const isAdmin = userRole === 'admin';

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: 56 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Argus
          </Typography>
          <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
            Operator Console
          </Typography>

          <Box sx={{ ml: 3, display: 'flex', gap: 0.5 }}>
            <Button
              size="small"
              color="inherit"
              startIcon={<DashboardIcon />}
              onClick={() => navigate('/dashboard')}
              sx={{ color: 'text.primary', textTransform: 'none' }}
            >
              Dashboard
            </Button>
            <Button
              size="small"
              color="inherit"
              startIcon={<GridViewIcon />}
              onClick={() => navigate('/wallboard')}
              sx={{ color: 'text.primary', textTransform: 'none' }}
            >
              Wallboard
            </Button>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <NotificationsBell />

          <Tooltip title={userName}>
            <IconButton size="small" onClick={handleAvatarClick}>
              <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: 'primary.main' }}>
                {userName.substring(0, 1).toUpperCase()}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {userName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {userRole}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => { openMyProfile(); handleClose(); }}>
              <AccountCircleIcon fontSize="small" sx={{ mr: 1 }} /> My profile
            </MenuItem>
            <MenuItem onClick={() => { onToggleTheme(); handleClose(); }}>
              {themeMode === 'dark' ? (
                <>
                  <LightModeIcon fontSize="small" sx={{ mr: 1 }} /> Light mode
                </>
              ) : (
                <>
                  <DarkModeIcon fontSize="small" sx={{ mr: 1 }} /> Dark mode
                </>
              )}
            </MenuItem>

            {isAdmin && (
              <Box>
                <Divider />
                <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary', textTransform: 'uppercase', fontSize: 11 }}>
                  Admin
                </Typography>
                <MenuItem onClick={() => { openUpdates(); handleClose(); }}>
                  <SystemUpdateAltIcon fontSize="small" sx={{ mr: 1 }} /> Updates Management
                </MenuItem>
                <MenuItem onClick={() => { openPermissions(); handleClose(); }}>
                  <PeopleAltIcon fontSize="small" sx={{ mr: 1 }} /> User Permissions
                </MenuItem>
                <MenuItem onClick={() => { openWatchedPaths(); handleClose(); }}>
                  <FolderSpecialIcon fontSize="small" sx={{ mr: 1 }} /> Watched Paths
                </MenuItem>
              </Box>
            )}

            <Divider />
            <MenuItem onClick={onLogout}>
              <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            backgroundColor: 'background.paper',
            borderRight: 1,
            borderColor: 'divider',
          },
        }}
      >
        <Toolbar variant="dense" />
        <Box sx={{ overflow: 'auto', height: '100%' }}>
          {sidebar ?? (
            <Box sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Agents sidebar
              </Typography>
            </Box>
          )}
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: '100vh',
          overflow: 'hidden',
          backgroundColor: 'background.default',
          position: 'relative',
        }}
      >
        <Toolbar variant="dense" />
        <Box
          className="argus-dashboard-canvas"
          sx={{
            position: 'absolute',
            top: 56,
            left: 0,
            right: 0,
            bottom: 40,
            overflow: 'hidden',
          }}
        >
          {children}
        </Box>
        <WindowsTaskbar />
      </Box>
    </Box>
  );
}
